export type LibraryGroupSummary = {
  key: string;
  rootRecordId: string | null;
};

export type LibraryRecordSummary = {
  id: string;
  group: string | null;
};

export type GroupsToExpandOptions = {
  groups: readonly Pick<LibraryGroupSummary, "key">[];
  visibleCountByGroup: ReadonlyMap<string, number>;
  query: string;
  expanded: ReadonlySet<string>;
  manuallyToggled: ReadonlySet<string>;
};

/** Counts the records rendered beneath each group heading. */
export function visibleItemCountsByGroup(
  groups: readonly LibraryGroupSummary[],
  records: readonly LibraryRecordSummary[]
): Map<string, number> {
  const roots = new Map(groups.map((group) => [group.key, group.rootRecordId]));
  const counts = new Map(groups.map((group) => [group.key, 0]));
  for (const record of records) {
    if (!record.group || !counts.has(record.group)) continue;
    if (record.id === roots.get(record.group)) continue;
    counts.set(record.group, (counts.get(record.group) || 0) + 1);
  }
  return counts;
}

/**
 * Derives the groups displayed open without changing the reader's explicit
 * expansion state. During a search, automatic expansion owns untouched groups;
 * a manual toggle overrides it until the term changes.
 */
export function groupsToExpand({
  groups,
  visibleCountByGroup,
  query,
  expanded,
  manuallyToggled
}: GroupsToExpandOptions): Set<string> {
  const result = new Set<string>();
  const filtering = query.trim().length >= 2;
  for (const group of groups) {
    if (!filtering) {
      if (expanded.has(group.key)) result.add(group.key);
      continue;
    }
    if (manuallyToggled.has(group.key)) {
      if (expanded.has(group.key)) result.add(group.key);
      continue;
    }
    const count = visibleCountByGroup.get(group.key) || 0;
    if (count > 0 && count < 5) result.add(group.key);
  }
  return result;
}

export type MenuFilterState = {
  chip: string;
  query: string;
  matchedIds: ReadonlySet<string> | null;
};

export type MenuTargetRecord = {
  id: string;
  kind: string;
};

/**
 * Which filters would hide `record` from the menu.
 *
 * An explicitly requested record — a deep link, a search result, an arrow step —
 * has to be reachable, and revealing a group the menu never rendered is
 * impossible. A remembered chip or search term can exclude it, so navigation
 * clears whichever of the two is in the way. A query with no matches resolved
 * yet counts as hiding: it is either still in flight or it failed, and re-running
 * it could remove the record after the reader has arrived.
 */
export function filtersHidingRecord(state: MenuFilterState, record: MenuTargetRecord): { chip: boolean; query: boolean } {
  // Filtering only starts at two characters, so a shorter draft hides nothing
  // and must not cost the reader their typing.
  const filtering = state.query.trim().length >= 2;
  return {
    chip: state.chip !== "all" && record.kind !== state.chip,
    query: filtering && (state.matchedIds === null || !state.matchedIds.has(record.id))
  };
}

/** Manual expansion overrides belong to one exact, trimmed search term. */
export function manualOverridesAfterQueryChange(
  previousQuery: string,
  nextQuery: string,
  manuallyToggled: ReadonlySet<string>
): Set<string> {
  return previousQuery.trim() === nextQuery.trim()
    ? new Set(manuallyToggled)
    : new Set<string>();
}
